import { PmeLivesTableSection } from '@/features/proposals/components/pme/steps/sections/lives-table-section.component';
import { PmeStipulantFormSection } from '@/features/proposals/components/pme/steps/sections/stipulant-form-section.component';
import {
  formatPercentInput,
  parsePercent,
} from '@/features/quotes/schemas/commercialization-percent.schema';
import { formatCurrencyBRL } from '@/features/quotes/utils/group-life/format';
import type { GroupLifeQuoteVersionItem } from '@/services';
import { Button, Spinner, Text } from '@noorden-insurtech/alba-design-system';
import {
  ArrowRight,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Pencil,
} from 'lucide-react';
import { useState } from 'react';
import {
  versionLivesDeltaKey,
  versionLivesDeltas,
} from '../version-lives-delta.util';
import {
  PME_ANALYSIS_COMMERCIALIZATION,
  PME_ANALYSIS_LIVES,
  PME_ANALYSIS_LIVES_TOTAL,
  PME_ANALYSIS_STIPULANTS,
  type PmeAnalysisCommercialization,
} from '../pme-analysis.mock';

/** Estilo padrão de título de seção (mesma classe usada nas seções de
 * cálculo/resumo do projeto, ex. `step-calculation.sections.tsx`). */
const SECTION_TITLE_CLASS = 'text-base text-alba-black-500';

const VERSIONS_GRID = 'grid-cols-[1fr_1fr_1fr_0.7fr_0.9fr_0.9fr_1fr_0.4fr]';

/** Header idêntico ao do DataTable do design system: thead `bg-alba-gray-200`
 *  + `border-b-2 border-alba-gray-300`, células uppercase. */
const VERSIONS_HEADER_CELL =
  'font-montserrat text-xs font-bold uppercase text-alba-black-500';

/** Taxa média chega como número (percentual) — formata em pt-BR. */
function formatRate(value: number): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });
}

// ─── Seção "Versões" ────────────────────────────────────────────────────────

export function PmeAnalysisVersionsSection({
  versions,
  onOpenVersion,
  onOpenCurrent,
  isLoading = false,
}: {
  versions: GroupLifeQuoteVersionItem[];
  /** Abre a tela de resumo hidratando a versão clicada. Recebe o item
   *  completo — versões `origin='SOURCE'` pertencem à proposta de ORIGEM
   *  (cadeia de clonagem) e o caller decide navegar para ela. */
  onOpenVersion: (version: GroupLifeQuoteVersionItem) => void;
  /** Abre o resumo da versão atual da cotação. */
  onOpenCurrent: () => void;
  isLoading?: boolean;
}) {
  const livesDeltas = versionLivesDeltas(versions);
  return (
    <div className='flex w-full flex-col gap-4 rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6'>
      <Text className={SECTION_TITLE_CLASS}>Versões</Text>

      <div className='overflow-x-auto'>
        <div
          className={`min-w-[760px] overflow-hidden rounded-xl border border-alba-gray-200`}
        >
          <div
            className={`grid ${VERSIONS_GRID} gap-3 border-b-2 border-alba-gray-300 bg-alba-gray-200 px-4 py-4`}
          >
            <Text className={VERSIONS_HEADER_CELL}>Tipo</Text>
            <Text className={VERSIONS_HEADER_CELL}>Data</Text>
            <Text className={VERSIONS_HEADER_CELL}>Número</Text>
            <Text className={VERSIONS_HEADER_CELL}>Vidas</Text>
            <Text className={VERSIONS_HEADER_CELL}>Afastados</Text>
            <Text className={VERSIONS_HEADER_CELL}>Taxa média</Text>
            <Text className={VERSIONS_HEADER_CELL}>Prêmio mensal</Text>
            <span />
          </div>

          {isLoading ? (
            <div className='flex justify-center py-10'>
              <Spinner size='md' colorScheme='blue' />
            </div>
          ) : versions.length === 0 ? (
            <div className='px-4 py-6'>
              <Text className='font-montserrat text-sm text-alba-gray-600'>
                Nenhuma versão encontrada.
              </Text>
            </div>
          ) : (
            versions.map((row, index) => {
              const prev = index > 0 ? versions[index - 1] : undefined;
              const isSource = row.origin === 'SOURCE';
              // Primeira linha herdada de cada proposta de origem — ganha um
              // separador mais forte para não se misturar com as versões da
              // própria proposta (e com as de outro ancestral da cadeia).
              const startsSourceGroup =
                isSource &&
                (!prev ||
                  prev.origin !== 'SOURCE' ||
                  prev.sourcePolicyId !== row.sourcePolicyId);
              const rowKey = versionLivesDeltaKey(row);
              const livesDelta = livesDeltas.get(rowKey);
              return (
                <div
                  key={rowKey}
                  className={`grid ${VERSIONS_GRID} items-center gap-3 px-4 py-4 ${
                    startsSourceGroup
                      ? 'border-t-2 border-alba-gray-300'
                      : index > 0
                        ? 'border-t border-alba-gray-200'
                        : ''
                  } ${isSource ? 'bg-alba-gray-100/40' : ''}`}
                >
                  <div className='flex flex-col items-start gap-1'>
                    <Text className='font-montserrat text-sm text-alba-black-500'>
                      {row.type}
                    </Text>
                    {isSource && (
                      <span className='whitespace-nowrap rounded-md bg-alba-gray-200 px-2 py-0.5 font-montserrat text-xs font-bold text-alba-gray-700'>
                        {row.number ? `Origem · ${row.number}` : 'Origem'}
                      </span>
                    )}
                  </div>
                  <div className='flex items-center gap-2 font-montserrat text-sm text-alba-gray-600'>
                    <CalendarDays className='h-4 w-4 text-alba-gray-600' />
                    <span>{row.date || '—'}</span>
                  </div>
                  <Text className='font-montserrat text-sm text-alba-gray-600'>
                    {row.number || '—'}
                  </Text>
                  <div className='flex flex-col'>
                    <Text className='font-montserrat text-sm text-alba-gray-600'>
                      {row.lives}
                    </Text>
                    {livesDelta ? (
                      // Desvio para a versão anterior (homologação 09-10: 611
                      // na cotação × 608 na proposta, sem explicação).
                      <span
                        title={livesDelta.reason}
                        className='cursor-help font-montserrat text-xs font-bold text-[#E8833A]'
                      >
                        {livesDelta.label}
                      </span>
                    ) : null}
                  </div>
                  <Text className='font-montserrat text-sm text-alba-gray-600'>
                    {row.removed}
                  </Text>
                  <Text className='font-montserrat text-sm text-alba-gray-600'>
                    {formatRate(row.averageRate)}
                  </Text>
                  <Text className='font-montserrat text-sm text-alba-gray-600'>
                    {formatCurrencyBRL(row.totalPremium / 12)}
                  </Text>
                  <button
                    type='button'
                    onClick={() => onOpenVersion(row)}
                    className='flex h-8 w-8 items-center justify-center rounded-lg text-alba-gray-600 hover:bg-alba-gray-100'
                    aria-label='Ver resumo da versão'
                  >
                    <ArrowRight className='h-4 w-4' />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className='flex justify-end'>
        <Button variant='secondary' size='md' onClick={onOpenCurrent}>
          Critério Atual
        </Button>
      </div>
    </div>
  );
}

// ─── Seção "Comercialização" ────────────────────────────────────────────────

function parsePercentValue(value: string): number {
  return parsePercent(value.replace('%', '').trim());
}

type CommercializationFieldName = keyof PmeAnalysisCommercialization;
type CommercializationErrors = Partial<
  Record<CommercializationFieldName, string>
>;

function validateCommercialization(
  value: PmeAnalysisCommercialization
): CommercializationErrors {
  const surcharge = parsePercentValue(value.surcharge);
  const actuarialDiscount = parsePercentValue(value.actuarialDiscount);
  const errors: CommercializationErrors = {};

  // Agravo não tem teto; desconto acima de 100% zeraria o prêmio.
  if (actuarialDiscount > 100) {
    errors.actuarialDiscount = 'O desconto atuarial não pode ultrapassar 100%.';
  }
  if (surcharge > 0 && actuarialDiscount > 0) {
    const message = 'Informe agravo ou desconto atuarial, não os dois.';
    errors.surcharge ??= message;
    errors.actuarialDiscount ??= message;
  }

  return errors;
}

function isSameCommercialization(
  a: PmeAnalysisCommercialization,
  b: PmeAnalysisCommercialization
): boolean {
  return (
    parsePercentValue(a.brokeragePercent) ===
      parsePercentValue(b.brokeragePercent) &&
    parsePercentValue(a.agencyPercent) === parsePercentValue(b.agencyPercent) &&
    parsePercentValue(a.surcharge) === parsePercentValue(b.surcharge) &&
    parsePercentValue(a.actuarialDiscount) ===
      parsePercentValue(b.actuarialDiscount)
  );
}

/** Remove o "%" para edição; garante o "%" para exibição. */
const stripPct = (v: string): string => (v ?? '').replace('%', '').trim();
const ensurePct = (v: string): string => {
  const s = stripPct(v);
  return s ? `${s}%` : '0%';
};

function CommercializationField({
  label,
  value,
  editing,
  invalid = false,
  error,
  onChange,
}: {
  label: string;
  value: string;
  editing: boolean;
  invalid?: boolean;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className='flex flex-col gap-2'>
      <Text className='font-montserrat text-sm font-bold text-alba-black-500'>
        {label}
      </Text>
      {editing ? (
        <input
          value={stripPct(value)}
          inputMode='numeric'
          placeholder='0,00'
          onChange={event => onChange(formatPercentInput(event.target.value))}
          className={`w-full rounded-lg border px-3 py-2 font-montserrat text-sm text-alba-black-500 outline-none ${
            invalid
              ? 'border-alba-error-red-500 focus:border-alba-error-red-500'
              : 'border-alba-gray-300 focus:border-alba-blue-500'
          }`}
        />
      ) : (
        <Text className='font-montserrat text-sm text-alba-gray-600'>
          {ensurePct(value)}
        </Text>
      )}
      {editing && error ? (
        <Text className='font-montserrat text-xs text-alba-error-red-500'>
          {error}
        </Text>
      ) : null}
    </div>
  );
}

export function PmeAnalysisCommercializationSection({
  value,
  onChange,
  onSave,
  showUnderwritingFields = true,
  readOnly = false,
}: {
  value?: PmeAnalysisCommercialization;
  onChange?: (value: PmeAnalysisCommercialization) => void;
  onSave?: () => Promise<boolean>;
  showUnderwritingFields?: boolean;
  /** Proposta cancelada: a comercialização fica só para leitura. */
  readOnly?: boolean;
} = {}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState<PmeAnalysisCommercialization | null>(
    null
  );
  const [internal, setInternal] = useState<PmeAnalysisCommercialization>(
    PME_ANALYSIS_COMMERCIALIZATION
  );
  const [errors, setErrors] = useState<CommercializationErrors>({});
  const data = value ?? internal;

  const patch = (partial: Partial<PmeAnalysisCommercialization>) => {
    // Agravo e desconto atuarial são mutuamente exclusivos, mas quem zera o
    // outro campo é o usuário: `validateCommercialization` sinaliza o conflito
    // e bloqueia o salvar. Zerar automaticamente apagava um valor já digitado.
    const next = { ...data, ...partial };
    setErrors(validateCommercialization(next));
    if (onChange) onChange(next);
    else setInternal(next);
  };

  const handleToggle = async () => {
    if (readOnly) return;
    if (!editing) {
      setBaseline(data);
      setErrors({});
      setEditing(true);
      return;
    }

    const nextErrors = validateCommercialization(data);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    const changed = !baseline || !isSameCommercialization(baseline, data);
    if (!changed || !onSave) {
      setEditing(false);
      return;
    }

    try {
      setSaving(true);
      const ok = await onSave();
      if (ok) setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='flex w-full flex-col gap-4 rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6'>
      <div className='flex items-center justify-between'>
        <Text className={SECTION_TITLE_CLASS}>Comercialização</Text>
        {!readOnly && (
          <Button
            variant='secondary'
            size='sm'
            className='flex items-center gap-2'
            loading={saving}
            disabled={saving}
            onClick={() => void handleToggle()}
          >
            {!editing && <Pencil className='h-4 w-4' />}
            {editing ? 'Salvar' : 'Editar'}
          </Button>
        )}
      </div>

      <div
        className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${
          showUnderwritingFields ? 'lg:grid-cols-4' : ''
        }`}
      >
        <CommercializationField
          label='Percentual da Corretagem'
          value={data.brokeragePercent}
          editing={editing}
          invalid={Boolean(errors.brokeragePercent)}
          error={errors.brokeragePercent}
          onChange={value => patch({ brokeragePercent: value })}
        />
        <CommercializationField
          label='Percentual do Agenciamento'
          value={data.agencyPercent}
          editing={editing}
          invalid={Boolean(errors.agencyPercent)}
          error={errors.agencyPercent}
          onChange={value => patch({ agencyPercent: value })}
        />
        {showUnderwritingFields && (
          <>
            <CommercializationField
              label='Agravo'
              value={data.surcharge}
              editing={editing}
              invalid={Boolean(errors.surcharge)}
              error={errors.surcharge}
              onChange={value => patch({ surcharge: value })}
            />
            <CommercializationField
              label='Desconto atuarial'
              value={data.actuarialDiscount}
              editing={editing}
              invalid={Boolean(errors.actuarialDiscount)}
              error={errors.actuarialDiscount}
              onChange={value => patch({ actuarialDiscount: value })}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Seção "Dados da Cotação" → Lista de Estipulantes ───────────────────────

function StipulantAccordion({
  label,
  isOpen,
  onToggle,
}: {
  label: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className='flex flex-col overflow-hidden rounded-xl border border-alba-gray-300'>
      <button
        type='button'
        className='flex w-full items-center justify-between px-4 py-4 text-left'
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <Text className='font-montserrat text-sm font-bold text-alba-black-500'>
          {label}
        </Text>
        {isOpen ? (
          <ChevronUp className='h-5 w-5 shrink-0 text-alba-gray-600' />
        ) : (
          <ChevronDown className='h-5 w-5 shrink-0 text-alba-gray-600' />
        )}
      </button>

      {isOpen && (
        <div className='flex flex-col gap-8 border-t border-alba-gray-300 px-4 py-6'>
          <PmeStipulantFormSection showContact />
          <PmeLivesTableSection
            showIncompleteStatus={false}
            lives={PME_ANALYSIS_LIVES}
            total={PME_ANALYSIS_LIVES_TOTAL}
          />
        </div>
      )}
    </div>
  );
}

export function PmeAnalysisStipulantsSection() {
  const stipulants = PME_ANALYSIS_STIPULANTS;
  const [openMap, setOpenMap] = useState<Record<number, boolean>>({});

  const allOpen = stipulants.every((_, index) => openMap[index]);

  const toggleAll = () => {
    const next = !allOpen;
    setOpenMap(
      stipulants.reduce<Record<number, boolean>>((acc, _, index) => {
        acc[index] = next;
        return acc;
      }, {})
    );
  };

  const toggleOne = (index: number) =>
    setOpenMap(prev => ({ ...prev, [index]: !prev[index] }));

  return (
    <div className='flex w-full flex-col gap-4 rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6'>
      <Text className={SECTION_TITLE_CLASS}>Dados da Cotação</Text>

      <div className='flex items-center justify-between'>
        <Text className='text-sm text-alba-black-500'>
          Lista de Estipulantes
        </Text>
        <button
          type='button'
          className='flex items-center gap-1 font-montserrat text-sm text-alba-blue-700'
          onClick={toggleAll}
        >
          {allOpen ? 'Fechar Todos' : 'Abrir Todos'} ({stipulants.length})
          {allOpen ? (
            <ChevronUp className='h-4 w-4' />
          ) : (
            <ChevronDown className='h-4 w-4' />
          )}
        </button>
      </div>

      <div className='flex flex-col gap-3'>
        {stipulants.map((label, index) => (
          <StipulantAccordion
            key={label}
            label={label}
            isOpen={Boolean(openMap[index])}
            onToggle={() => toggleOne(index)}
          />
        ))}
      </div>
    </div>
  );
}
